import * as React from 'react';

import { initRevieve } from '@notino/analyzer-tools';

import { useApolloClientHeaders } from '@hooks/useApolloClientHeaders';

import { HairAnalyzerConfig } from '../../model';
import { HAIR_ANALYZER_OPEN_HASH } from '../constants';
import { HairRevieveConfig } from '../model';
import { haCountryConfig } from '../utils';

import { useCombinedTracking } from './useCombinedTracking';
import { useHairAnalyzerEvents } from './useHairAnalyzerEvents';

interface UseInitHairAnalyzerArgs {
  brand: HairAnalyzerConfig;
}

export const useInitHairAnalyzer = ({ brand }: UseInitHairAnalyzerArgs) => {
  const {
    shopId,
    hairAnalyzer: { env },
  } = useApolloClientHeaders();

  const countryConfig = haCountryConfig[shopId];

  const { events } = useHairAnalyzerEvents();
  const onTrackingEvent = useCombinedTracking({ brand });

  React.useEffect(() => {
    if (!countryConfig) {
      return;
    }

    const {
      onAddToCart,
      onAddToFavorites,
      onRemoveFromFavorites,
      onCheckout,
      onClickProduct,
      onExpandProduct,
    } = events;

    const revieveConfig: HairRevieveConfig = {
      partner_id: countryConfig.partnerId,
      locale: countryConfig.locale,
      env,
      onAddToCart: async (products) => {
        await onAddToCart(products);
      },
      onAddToFavorites: async (product) => {
        await onAddToFavorites(product.id);
      },
      onRemoveFromFavorites: async (product) => {
        await onRemoveFromFavorites(product.id);
      },
      onCheckout: (_) => {
        onCheckout();
      },
      onClickProduct: (product) => {
        onClickProduct(product);
      },
      onExpandProduct: (product) => {
        onExpandProduct(product);
      },
      onTrackingEvent,
      recommendationFilters: {
        brand_includes: brand.brands ?? [],
      },
    };

    // Hash-driven: nothing is fetched or initialized until the user opens the
    // tool. initRevieve also owns hide/unmount and the hash cleanup on close.
    return initRevieve(HAIR_ANALYZER_OPEN_HASH, revieveConfig);
  }, [countryConfig, env, events, brand, onTrackingEvent]);
};
